import { createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import { SALARIES_TRANSFER_SEND } from 'scenes/Permissions/constants/permissions';
import { selectIsRequiredBusinessPermissionFromCurrentActiveEmployerBusiness } from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { generateReportFromRecords } from 'scenes/WorkDemo/utils/report-generation/generateReportFromRecords';
import { selectAssignmentsArray } from 'store/assignments/selectors/assignment.selectors';
import { selectProjectUsersArray } from 'store/projectUsers/selectors/projectUsers.selectors';
import { loadEmployeeConfigurationsInBusiness } from 'store/projectUsers/thunks/loadEmployeeConfigurationsInBusiness.thunk';
import { selectSelectedProject } from 'store/projects/selectors/projectsSelectors';
import { selectRecordAllowancesGroupedByRecordId } from 'store/recordAllowances/selectors/record-and-employer-allowances.selectors';
import { selectSelectedRecordsIds } from 'store/records/selectors/records-base.selectors';
import { selectWorkCalendarAllowanceTypeRecordsGroupedByParentRecordId } from 'store/records/selectors/records.selectors';
import {
  selectIsIncludeOnlyApprovedRecordsEnabled,
  selectIsRecordBasedReductionsActive,
  selectIsReportProjectHourRowsEnabled,
  selectReportGeneratedFromMode,
} from 'store/reports/selectors/reports.selectors';
import {
  selectProjectsDataResourcesArrayWithEmployeeConfigurationInformation,
  selectWorkCalendarFilteredRecords,
  selectWorkCalendarProjectsArray,
} from '../../selectors/workSelectorsDemo';

export const generateSalaryRowDataFromRecords = createAsyncThunk(
  'workCalendar/generateSalaryRowDataFromRecords',
  async (dialogSettings, { getState, dispatch }) => {
    const selectedProject = selectSelectedProject(getState());

    const selectedRecordIds = selectSelectedRecordsIds(getState());

    const reportGeneratedFromMode = selectReportGeneratedFromMode(getState());

    const projects = selectWorkCalendarProjectsArray(getState());

    const allWorkCalendarFilteredRecords =
      selectWorkCalendarFilteredRecords(getState());

    const assignments = selectAssignmentsArray(getState());

    const users = selectProjectUsersArray(getState());

    const isRecordBasedReductions =
      selectIsRecordBasedReductionsActive(getState());

    const isReportProjectHourRowsEnabled =
      selectIsReportProjectHourRowsEnabled(getState());

    const isPermissionToSendSalaries =
      selectIsRequiredBusinessPermissionFromCurrentActiveEmployerBusiness(
        getState(),
        SALARIES_TRANSFER_SEND
      );

    const recordAllowancesGroupedByRecordId =
      selectRecordAllowancesGroupedByRecordId(getState());

    const allowanceRecordsGroupedByParentRecordId =
      selectWorkCalendarAllowanceTypeRecordsGroupedByParentRecordId(getState());

    const isIncludeOnlyApprovedRecordsEnabled =
      selectIsIncludeOnlyApprovedRecordsEnabled(getState());

    const reportRecords =
      reportGeneratedFromMode === 'SELECTED'
        ? allWorkCalendarFilteredRecords.filter((record) =>
            selectedRecordIds.includes(record.id)
          )
        : allWorkCalendarFilteredRecords;

    const recordsToGenerateReportFrom = isIncludeOnlyApprovedRecordsEnabled
      ? reportRecords.filter((record) => record.approved)
      : reportRecords;

    const utcOffset = moment.parseZone(moment().format()).format('Z');

    const {
      selectedReportType,
      workStartTime,
      workEndTime,
      fromDate,
      toDate,
      language,
      filetype,
      workHourType,
      paidCoffeeBreakMinutes,
    } = dialogSettings;

    const generationParams = {
      fromDate: moment(fromDate).utcOffset(0, true).format(),
      toDate: moment(toDate).utcOffset(0).format(),
      selectedReportType,
      workStartTime,
      workEndTime,
      language,
      filetype,
      utcOffset,
      workHourType,
      isRecordBasedReductions,
      paidCoffeeBreakMinutes,
      isProjectHourRowsEnabled: isReportProjectHourRowsEnabled,
    };

    if (isPermissionToSendSalaries) {
      await dispatch(loadEmployeeConfigurationsInBusiness());
    }

    const resources =
      selectProjectsDataResourcesArrayWithEmployeeConfigurationInformation(
        getState()
      );

    const report = await generateReportFromRecords(
      recordsToGenerateReportFrom,
      resources,
      assignments,
      users,
      generationParams,
      projects,
      selectedProject,
      recordAllowancesGroupedByRecordId,
      allowanceRecordsGroupedByParentRecordId
    );

    return report;
  }
);
